import { PropsWithChildren } from "react";
import { useEntityContext } from "./useEntityContext";
import { Avatar, Pagination, Spin, Tooltip, theme } from "antd";
import {
  entityContributorsContext,
  useEntityContributors,
  useEntityContributorsContext,
} from "./useEntityContributors";
import { TagsList } from "./components/TagsList";
import { CollapsibleRow } from "./components/CollapsibleRow";
import { EntitySummary, ExpandedView } from "./components/EntitySummary";
import { Flex } from "src/components/Flex";
import { VzblTag } from "./components/VzblTag";
import { Section } from "./components/Section";
import { EntityLink } from "./components/EntityLink";
import { useApplicationContext } from "src/hooks/useApplication";

export const Contributors = (
  props: PropsWithChildren<{
    degrees?: number;
  }>
) => {
  const { query } = useEntityContext();
  const orgId = useApplicationContext().org_id!;

  const entityCollaborators = useEntityContributors({
    orgId,
    entityUuid: query.data.entity.properties.uuid,
    degrees: props.degrees || 1,
  });
  return (
    <entityContributorsContext.Provider value={entityCollaborators}>
      {props.children}
    </entityContributorsContext.Provider>
  );
};

export const ContributorsAvatarComponent = (_props: { title?: string }) => {
  // const orgId = useSessionContext().org_id!;
  // const context = useEntityContext();
  // const { token } = theme.useToken();
  const { query } = useEntityContributorsContext();
  if (!query.isFetched) {
    query.refetch();
  }
  if (query.isLoading) {
    return <Spin></Spin>;
  }
  return (
    <Avatar.Group
      maxCount={2}
      maxStyle={{ color: "#f56a00", backgroundColor: "#fde3cf" }}
    >
      {query.data?.data?.map((item: any, index: number) => (
        <EntityLink
          uuid={item.actor.properties.uuid}
          className="entity"
          key={index}
        >
          <Tooltip title={item.actor.properties.name}>
            <Avatar shape="circle" src={item.actor.properties.avatarUrl}>
              {item.actor.properties.name
                ?.split(" ")
                ?.map((name: string) => name[0].toLocaleUpperCase())
                ?.join("")}
            </Avatar>
          </Tooltip>
        </EntityLink>
      ))}
    </Avatar.Group>
  );
};

export const ContributorsComponent = (props: { title?: string }) => {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const { token } = theme.useToken();
  const { query, setPagination } = useEntityContributorsContext();
  if (!query.isFetched) {
    query.refetch();
  }
  return (
    <Section
      title={props.title}
      isEmpty={query.data?.data.length === 0}
      style={
        query.data?.data.length === 0
          ? { color: token.colorText }
          : { paddingTop: 0, color: token.colorText }
      }
    >
      {query.isLoading && <Spin />}
      {query.data?.data.length === 0 && (
        <div style={{ color: token.colorTextTertiary }}>None</div>
      )}
      {query.data?.data?.map((item: any, index: number) => {
        return (
          <CollapsibleRow
            key={item.actor.properties.uuid}
            style={{
              borderBottom: `1px solid ${token.colorBorderSecondary}99`,
            }}
            content={
              <ExpandedView>
                <EntitySummary entity={item.actor} key={index} />
              </ExpandedView>
            }
            preview={
              <Flex
                spaceBetween
                style={{ alignItems: "center", lineHeight: "30px" }}
              >
                <div
                  className="trunc entity"
                  style={{ marginLeft: 5, lineHeight: "30px" }}
                >
                  <Avatar
                    shape="circle"
                    size="small"
                    src={item.actor.properties.avatarUrl}
                  >
                    {item.actor.properties.name
                      ?.split(" ")
                      ?.map((name: string) => name[0].toLocaleUpperCase())
                      ?.join("")}
                  </Avatar>
                  <Tooltip title={item.actor.properties.identity}>
                    <EntityLink
                      uuid={item.actor.properties.uuid}
                      className="entity"
                    >
                      {item.actor.properties.name}
                    </EntityLink>
                  </Tooltip>
                </div>
                <TagsList
                  tags={item.relations}
                  threshold={1}
                  color="default"
                  style={{ maxWidth: "50%" }}
                  tag={(tag: string, index: number) => (
                    <VzblTag
                      orgId={orgId}
                      context={context}
                      text={tag}
                      key={index}
                    />
                  )}
                />
              </Flex>
            }
          />
        );
      })}
      {query.data && (
        <Pagination
          size="small"
          hideOnSinglePage={true}
          current={query.data?.meta.page}
          total={query.data?.meta.total}
          pageSize={query.data?.meta.pageSize}
          showSizeChanger={false}
          showTotal={(t, [r1, r2]) =>
            (r1 > 1 || r2 < t) && (
              <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                showing {r1}-{r2} of {t}
              </div>
            )
          }
          onChange={(page, pageSize) => {
            setPagination({ page, pageSize });
          }}
          style={{ textAlign: "right", marginTop: 10 }}
        />
      )}
    </Section>
  );
};
